import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Compass, PlayCircle, Brain, Exam, ArrowRight, Sparkle,
  ArrowsClockwise, Trophy, Lightning, Star, BookOpen, Chats,
  ShieldCheck, ListChecks, Sparkle as SparkleIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";

import { API_BASE as API } from "../lib/apiBase";

// Vincent B816 (Eric 2026-02-13) — Style visuel par type de KU dans la
// carte « Connaissances clés ». On garde un badge minuscule uniforme pour
// ne pas alourdir la page (B819 → réduction verticale).
// Tâche 1419 (Eric 2026-09-30) — Les étiquettes de type portent leurs deux
// langues sur la même ligne : c'est du texte d'interface, pas de la donnée.
const KU_TYPE_META = {
  qa:         { fr: "Q&R",       en: "Q&A",       icon: Chats,       color: "text-violet-600" },
  synthesis:  { fr: "Synthèse",  en: "Synthesis", icon: Sparkle,     color: "text-teal-600" },
  script:     { fr: "Script",    en: "Script",    icon: BookOpen,    color: "text-amber-700" },
  objection:  { fr: "Objection", en: "Objection", icon: ShieldCheck, color: "text-rose-600" },
  procedure:  { fr: "Procédure", en: "Procedure", icon: ListChecks,  color: "text-blue-600" },
  concept:    { fr: "Concept",   en: "Concept",   icon: Brain,       color: "text-indigo-600" },
};

export default function DiscoveryPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { lang, tr } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Vincent B820 (Eric 2026-02-13) — Dropdown admin/concepteur pour choisir
  // le parcours source. Chargé via /api/pathways-onboarding/list.
  const [pathwayOptions, setPathwayOptions] = useState([]);
  const [selectedPathwayId, setSelectedPathwayId] = useState("");
  const [switching, setSwitching] = useState(false);

  const fetchContent = useCallback(async (pid) => {
    setLoading(true);
    try {
      // Tâche 1419 — Le libellé de rôle est calculé par le serveur : il doit
      // savoir dans quelle langue l'écran est regardé.
      const q = pid ? `?pathway_id=${encodeURIComponent(pid)}&lang=${lang}` : `?lang=${lang}`;
      const r = await axios.get(`${API}/api/discovery/content${q}`, { withCredentials: true });
      setData(r.data);
      // Sync la sélection avec ce que le backend a réellement choisi
      // (utile quand aucun override et que le flag est_default est actif).
      if (r.data?.pathway?.id) setSelectedPathwayId(r.data.pathway.id);
    } catch (e) {
      toast.error(tr("Erreur de chargement", "Loading failed"));
    } finally {
      setLoading(false);
    }
  }, [lang, tr]);

  useEffect(() => { fetchContent(); }, [fetchContent]);

  // Charge la liste des parcours d'intégration (dropdown B820) UNIQUEMENT
  // pour les admins/concepteurs. `can_edit` vient du backend.
  useEffect(() => {
    if (!data?.can_edit) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/pathways-onboarding/list`, { withCredentials: true });
        setPathwayOptions(r.data?.pathways || []);
      } catch { /* non-fatal — le dropdown reste vide */ }
    })();
  }, [data?.can_edit]);

  // Vincent B820 — Le choix admin est persistant au niveau ORG :
  // POST /api/pathways/{id}/set-default-onboarding met à jour le flag.
  const applyDefaultPathway = async (pid) => {
    if (!pid || switching) return;
    setSwitching(true);
    try {
      await axios.post(`${API}/api/pathways/${pid}/set-default-onboarding`, {}, { withCredentials: true });
      toast.success(tr("Parcours par défaut mis à jour pour toute l'organisation", "Default pathway updated for the whole organisation"));
      await fetchContent();
      // Refresh dropdown to reflect new default
      const r = await axios.get(`${API}/api/pathways-onboarding/list`, { withCredentials: true });
      setPathwayOptions(r.data?.pathways || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || tr("Erreur lors du changement de parcours", "Could not change the pathway"));
    } finally {
      setSwitching(false);
    }
  };

  const stats = data?.stats || {};
  const pathway = data?.pathway || null;
  const kus = data?.knowledge_units || [];
  const firstName = (user?.name || "").split(" ")[0] || "";
  const isEmpty = !data?.videos?.length;

  // Vincent B819 — Bande hero compacte (avant : py-8/py-10 + hauteur 240 →
  // maintenant py-5/py-6, absoluteSize du Compass réduite).
  const heroClass = "relative overflow-hidden rounded-2xl bg-gradient-to-br from-teal-600 to-teal-800 text-white p-5 lg:p-6";

  if (loading && !data) {
    return (
      <div className="text-center py-16 text-muted-foreground text-sm">
        <ArrowsClockwise size={28} className="mx-auto mb-2 animate-spin opacity-50" />
        {tr("Préparation de votre parcours…", "Preparing your pathway…")}
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-4 pb-8 animate-fade-in stagger-reveal" data-testid="discovery-page">
      {/* Hero — compact */}
      <div className={heroClass} data-testid="discovery-hero">
        <div className="absolute top-0 right-0 opacity-10">
          <Compass size={160} weight="duotone" />
        </div>
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-1.5">
            <Sparkle size={14} weight="fill" className="text-amber-200" />
            <span className="text-[10px] uppercase tracking-[0.2em] font-bold opacity-90">{tr("Initiation", "Onboarding")}</span>
            {pathway?.name && (
              <span className="text-[10px] uppercase tracking-wide font-semibold bg-white/15 px-2 py-0.5 rounded-full" data-testid="discovery-pathway-badge">
                {pathway.name}
              </span>
            )}
          </div>
          <h1 className="text-2xl lg:text-3xl font-bold mb-1.5 leading-tight" data-testid="discovery-title">
            {tr("Bienvenue", "Welcome")}{firstName ? `, ${firstName}` : ""}
          </h1>
          {/* Vincent B817 — Texte simplifié : « parcours d'intégration personnalisé. » + rôle, SANS pourcentage. */}
          <p className="text-sm opacity-90 max-w-2xl mb-3 leading-relaxed" data-testid="discovery-tagline">
            {tr("Voici votre parcours d'intégration personnalisé.", "Here is your personalised onboarding pathway.")}
            {data?.role_label ? <> <span className="font-semibold">{data.role_label}</span>.</> : null}
          </p>
          {/* Progress bar compacte */}
          {!isEmpty && (
            <div className="bg-white/20 rounded-full h-2 max-w-md overflow-hidden mb-1">
              <div
                className="bg-white h-full rounded-full transition-all duration-700"
                style={{ width: `${stats.progress_pct || 0}%` }}
                data-testid="discovery-progress-bar"
              />
            </div>
          )}
          <div className="text-xs opacity-90">
            {isEmpty
              ? (pathway
                  ? tr("Ce parcours ne contient aucune vidéo complète pour l'instant.", "This pathway has no fully processed video yet.")
                  : tr("Aucun parcours d'intégration configuré — demandez à un admin.", "No onboarding pathway configured — ask an admin."))
              : tr(
                  `${stats.completed_videos || 0} / ${stats.total_videos || 0} vidéos complétées · ${stats.progress_pct || 0}%`,
                  `${stats.completed_videos || 0} / ${stats.total_videos || 0} videos completed · ${stats.progress_pct || 0}%`,
                )}
          </div>
        </div>
      </div>

      {/* Vincent B820 — Dropdown admin/concepteur pour changer le parcours source */}
      {data?.can_edit && pathwayOptions.length > 0 && (
        <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-xl px-4 py-2.5 flex items-center gap-3 text-sm" data-testid="discovery-admin-picker">
          <SparkleIcon size={14} weight="fill" className="text-amber-600 shrink-0" />
          <span className="text-amber-900 dark:text-amber-200 font-medium shrink-0">{tr("Designer", "Designer")} :</span>
          <select
            value={selectedPathwayId}
            onChange={(e) => applyDefaultPathway(e.target.value)}
            disabled={switching}
            className="flex-1 min-w-0 bg-white dark:bg-zinc-900 border border-amber-300 dark:border-amber-700 rounded px-2 py-1 text-sm disabled:opacity-60"
            data-testid="discovery-pathway-select"
          >
            {pathwayOptions.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} {p.is_default_onboarding ? tr("· (par défaut)", "· (default)") : ""} · {tr(`${p.video_count} vidéo${p.video_count !== 1 ? "s" : ""}`, `${p.video_count} video${p.video_count !== 1 ? "s" : ""}`)}
              </option>
            ))}
          </select>
          <span className="text-[11px] text-amber-800 dark:text-amber-300 shrink-0 hidden md:inline">
            {tr("Change pour toute l'organisation", "Applies to the whole organisation")}
          </span>
        </div>
      )}

      {isEmpty ? null : (
        <>
          {/* Step 1: Videos — vidéos du parcours choisi */}
          <section data-testid="discovery-videos">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 rounded-lg bg-[#002FA7] flex items-center justify-center text-white text-xs font-bold">1</div>
              <h2 className="text-sm font-bold flex items-center gap-1.5">
                <PlayCircle size={16} weight="duotone" className="text-[#002FA7]" />
                {tr("Vidéos essentielles", "Essential videos")}
              </h2>
              <span className="text-[11px] text-muted-foreground">
                {tr(
                  `${data.videos.length} vidéo${data.videos.length > 1 ? "s" : ""} du parcours`,
                  `${data.videos.length} video${data.videos.length > 1 ? "s" : ""} in the pathway`,
                )}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {data.videos.map((v, i) => (
                <div
                  key={v.id}
                  onClick={() => navigate(`/dashboard/video/${v.id}`)}
                  className="group bg-card border border-border rounded-xl p-3 cursor-pointer hover:border-[#002FA7]/50 hover:shadow-md transition-all"
                  data-testid={`discovery-video-${i}`}
                >
                  <div className="flex items-start gap-2.5">
                    <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-[#002FA7]/10 flex items-center justify-center">
                      <PlayCircle size={16} weight="fill" className="text-[#002FA7]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">
                          {tr("Étape", "Step")} {i + 1}
                        </span>
                        {v.training_theme && (
                          <span className="text-[9px] uppercase tracking-wider font-bold text-amber-600 truncate">
                            {v.training_theme}
                          </span>
                        )}
                      </div>
                      <h3 className="font-semibold text-sm leading-tight group-hover:text-[#002FA7] transition-colors line-clamp-2">
                        {v.title}
                      </h3>
                      <div className="flex items-center gap-2 mt-1.5 text-[10px] text-muted-foreground">
                        {v.duration && <span>{Math.round(v.duration / 60)} min</span>}
                        <ArrowRight size={11} className="ml-auto group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Step 2: 5 Connaissances clés (B816 — 2 qa + 1 synthesis + 2 mix) */}
          {kus.length > 0 && (
            <section data-testid="discovery-knowledge">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 rounded-lg bg-violet-600 flex items-center justify-center text-white text-xs font-bold">2</div>
                <h2 className="text-sm font-bold flex items-center gap-1.5">
                  <Brain size={16} weight="duotone" className="text-violet-600" />
                  {tr("Connaissances clés", "Key knowledge")}
                </h2>
                <span className="text-[11px] text-muted-foreground">
                  {tr(
                    `${kus.length} extrait${kus.length > 1 ? "s" : ""} des vidéos du parcours`,
                    `${kus.length} excerpt${kus.length > 1 ? "s" : ""} from the pathway videos`,
                  )}
                </span>
              </div>
              <div className="bg-card border border-border rounded-xl divide-y divide-border">
                {kus.map((k, i) => {
                  const meta = KU_TYPE_META[k.type] || KU_TYPE_META.qa;
                  const Icon = meta.icon;
                  const preview =
                    k.type === "qa"        ? (k.content?.answer || k.content?.question) :
                    k.type === "synthesis" ? (k.content?.summary || k.content?.text) :
                    k.type === "objection" ? (k.content?.response || k.content?.phrase_evitement || k.content?.question) :
                    k.type === "script"    ? (k.content?.scenario || k.content?.text) :
                    k.type === "procedure" ? (k.content?.summary || (Array.isArray(k.content?.steps) ? k.content.steps.slice(0,2).join(" → ") : "")) :
                    k.type === "concept"   ? (k.content?.definition || k.content?.summary || k.content?.text) :
                    "";
                  // Vincent B816 v2 (Eric 2026-02-13) — Chaque carte est cliquable
                  // → navigation vers la vidéo source avec `?focus=<ku_id>` qui
                  // ouvre automatiquement le KnowledgeDetailModal sur ce KU
                  // (support existant via useVideoDeepLink, P1.8.b).
                  const goToKu = () => k.video_id && navigate(`/dashboard/video/${k.video_id}?focus=${k.id}`);
                  return (
                    <div
                      key={k.id}
                      onClick={goToKu}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); goToKu(); } }}
                      className="p-2.5 hover:bg-secondary/40 transition-colors cursor-pointer group"
                      data-testid={`discovery-ku-${i}`}
                    >
                      <div className="flex items-start gap-2.5">
                        <Icon size={14} weight="fill" className={`${meta.color} flex-shrink-0 mt-0.5`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-[9px] uppercase tracking-wide font-bold ${meta.color}`} data-testid={`discovery-ku-type-${i}`}>
                              {lang === "en" ? meta.en : meta.fr}
                            </span>
                            <h4 className="font-semibold text-sm leading-tight group-hover:text-[#002FA7] transition-colors">{k.title}</h4>
                          </div>
                          {preview && <p className="text-xs mt-0.5 leading-relaxed line-clamp-2 text-muted-foreground">{preview}</p>}
                          {k.video_title && (
                            <p className="text-[9px] text-muted-foreground mt-1 flex items-center gap-1">
                              <BookOpen size={9} /> {k.video_title}
                            </p>
                          )}
                        </div>
                        <ArrowRight size={12} className="text-muted-foreground opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all shrink-0 mt-1" />
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* Vincent B818 v2 (Eric 2026-02-13) — La carte quiz est TOUJOURS
              visible tant qu'il y a un parcours (`pathway`), même si le backend
              n'a pas trouvé de questions préconstruites : la page Quiz gère la
              génération à la volée depuis le paramètre `?pathway_id=`. */}
          {pathway?.id && (
            <section data-testid="discovery-quiz">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 rounded-lg bg-amber-500 flex items-center justify-center text-white text-xs font-bold">3</div>
                <h2 className="text-sm font-bold flex items-center gap-1.5">
                  <Exam size={16} weight="duotone" className="text-amber-500" />
                  {tr("Quiz d'intégration", "Onboarding quiz")}
                </h2>
                <span className="text-[11px] text-muted-foreground">
                  {tr("Questions issues des vidéos du parcours", "Questions taken from the pathway videos")}
                </span>
              </div>
              <div
                onClick={() => navigate(`/dashboard/quiz?pathway_id=${pathway.id}`)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate(`/dashboard/quiz?pathway_id=${pathway.id}`); } }}
                className="bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-950/30 dark:to-amber-950/10 border border-amber-300/50 rounded-xl p-3.5 cursor-pointer hover:border-amber-400 transition-all group"
                data-testid="discovery-quiz-card"
              >
                <div className="flex items-center gap-3">
                  <Trophy size={26} weight="duotone" className="text-amber-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-sm">{data.quiz?.title || tr("Quiz d'intégration", "Onboarding quiz")}</h3>
                    <p className="text-[11px] text-amber-700 dark:text-amber-300 mt-0.5">
                      {data.quiz?.empty
                        ? tr(
                            `Générer un quiz à partir des ${data.videos.length} vidéo${data.videos.length !== 1 ? "s" : ""} du parcours`,
                            `Generate a quiz from the ${data.videos.length} video${data.videos.length !== 1 ? "s" : ""} in the pathway`,
                          )
                        : tr(
                            `${data.quiz?.questions?.length || 5} questions · basé sur ce parcours`,
                            `${data.quiz?.questions?.length || 5} questions · based on this pathway`,
                          )}
                    </p>
                  </div>
                  <ArrowRight size={16} className="text-amber-600 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </section>
          )}

          {/* Footer CTA — minimaliste */}
          <div className="bg-card border border-border rounded-xl px-4 py-3 flex items-center gap-3">
            <Lightning size={20} weight="duotone" className="text-[#002FA7] shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold">{tr("Prêt à approfondir ?", "Ready to go further?")}</p>
              <p className="text-[11px] text-muted-foreground">{tr("Posez vos questions à l'assistant Knoria.", "Ask the Knoria assistant your questions.")}</p>
            </div>
            <Button
              onClick={() => navigate("/dashboard/chat")}
              size="sm"
              className="gap-1.5 bg-[#002FA7] hover:bg-[#002FA7]/90 text-white h-8 text-xs shrink-0"
              data-testid="discovery-go-chat"
            >
              <Brain size={13} /> Knoria
              <ArrowRight size={12} />
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
